import { action, flow, makeAutoObservable, observable } from 'mobx';
import { BaseWallet } from './BaseWallet';
import EVMBase from './EVMBase';
import {
	signTransaction,
	WalletWithRequiredFeatures,
	SuiSignTransactionInput,
	SuiReportTransactionEffectsInput,
} from '@mysten/wallet-standard';
import type { Transaction } from '@mysten/sui/transactions';
import { toBase64 } from '@mysten/sui/utils';
import SUIConnector from '../SUI';
import type { StandardEventsChangeProperties, WalletAccount } from '@wallet-standard/core';
import { toGenerator } from '../../utils';

export const SUI_CHAINID = 1999;

function suiAccountFilter(account: WalletAccount) {
	return account.chains.some((chain) => chain.split(':')[0] === 'sui');
}

export default class SuiWallet implements BaseWallet {
	readonly type = 'sui';
	name: string = '';
	icon: string = '';
	info = undefined;
	website = undefined;
	account: WalletAccount | undefined = undefined;

	adapter: WalletWithRequiredFeatures;
	readonly chainId = SUI_CHAINID;
	readonly supportedChainIds = Object.freeze([SUI_CHAINID]);
	private connector: SUIConnector;
	private supportedIntents: string[] = [];

	private disposers: (() => void)[] = [];

	connecting = false;
	connected = false;

	constructor(connector: SUIConnector, adapter: WalletWithRequiredFeatures) {
		this.connector = connector;
		this.adapter = adapter;
		this.name = adapter.name;
		this.icon = adapter.icon;
		makeAutoObservable<SuiWallet, 'connector' | 'disposers'>(
			this,
			{ adapter: observable.ref, connector: false, disposers: false, dispose: false },
			{ autoBind: true },
		);
		this.disposers.push(this.adapter.features['standard:events'].on('change', this.handleChannge));
	}

	dispose() {
		this.disposers.forEach((f) => f());
	}

	isEVM(): this is EVMBase {
		return false;
	}

	get selectedAddress() {
		return this.account?.address;
	}

	get isReady() {
		return Boolean(this.connected && this.selectedAddress);
	}

	get disconnectable() {
		return this.connected;
	}

	private get client() {
		return this.connector.client;
	}
	connect = flow(function* connect(this: SuiWallet) {
		this.connecting = true;
		try {
			const result = yield* toGenerator(this.adapter.features['standard:connect'].connect());
			this.account = result.accounts.filter(suiAccountFilter)[0];
			if (this.account) {
				this.connected = true;
				this.supportedIntents = result.supportedIntents ?? [];
			}
		} finally {
			this.connecting = false;
		}
	});
	disconnect = flow(function* disconnect(this: SuiWallet) {
		yield this.adapter.features['standard:disconnect']?.disconnect();
		this.account = undefined;
		this.connected = false;
	});

	supports(chain: Readonly<Chain> | Chain | number): boolean {
		const chainId = typeof chain !== 'number' ? chain.chainId : chain;
		return this.supportedChainIds.includes(chainId);
	}

	async sign({ transaction, ...rest }: SuiSignTransactionInput & { transaction: Transaction }) {
		if (!this.account || !this.connected) {
			throw new Error('SUI: No wallet connected');
		}
		const client = this.client;
		const supportedIntents = this.supportedIntents.concat();
		return signTransaction(this.adapter, {
			transaction: {
				async toJSON() {
					return typeof transaction === 'string'
						? transaction
						: await transaction.toJSON({
								supportedIntents,
								client,
						  });
				},
			},
			...rest,
		});
	}

	async signAndExecute(transaction: Transaction): Promise<string> {
		if (!this.account || !this.connected) {
			throw new Error('SUI: No wallet connected');
		}
		const account = this.account;
		const chain = this.account.chains[0];
		const client = this.client;
		const { bytes, signature } = await this.sign({ transaction, chain, account });
		const result = await client.executeTransactionBlock({
			transactionBlock: bytes,
			signature,
			options: {
				showRawEffects: true,
			},
		});
		const digest = result.digest;
		await client.waitForTransaction({
			digest,
		});

		let effects: string;
		if ('effects' in result && result.effects && 'bcs' in result.effects && result.effects?.bcs) {
			effects = result.effects.bcs as string;
		} else if ('rawEffects' in result) {
			effects = toBase64(new Uint8Array(result.rawEffects!));
		} else {
			throw new Error('Could not parse effects from transaction result.');
		}

		this.reportTransactionEffects({ effects, account, chain });

		return digest;
	}

	private async reportTransactionEffects({ effects, account, chain }: SuiReportTransactionEffectsInput) {
		const reportTransactionEffectsFeature = this.adapter.features['sui:reportTransactionEffects'];
		if (!reportTransactionEffectsFeature) return;
		return await reportTransactionEffectsFeature.reportTransactionEffects({
			effects: Array.isArray(effects) ? toBase64(new Uint8Array(effects)) : effects,
			account,
			chain,
		});
	}

	private handleChannge = action((properties: StandardEventsChangeProperties) => {
		if (properties.accounts) {
			this.account = properties.accounts.filter(suiAccountFilter)[0];
		}
	});
}
